/**
 * 单张项目资源占用卡（simulation / simulation-gpu）。
 *
 * 视觉规范来自 docs/mockups/overview-status-ui.html 第二区,四行主指标
 * (CPU / 内存 / GPU / 运行中任务) + 底部三行只读参数。
 */
import React from "react";
import { Card, Typography, Tag, Skeleton, Alert } from "antd";
import { LineChartOutlined, EditOutlined } from "@ant-design/icons";
import type { OverviewResourceItem } from "@/apis/overview";

const { Text } = Typography;

interface Props {
  item: OverviewResourceItem;
  loading?: boolean;
  emoji: string;
  /** 卡头两个 ghost 按钮的点击回调,禁用状态时不触发。 */
  onOpenTrend?: () => void;
  onEditQuota?: () => void;
}

interface MetricRowSpec {
  key: string;
  label: string;
  /** 进度条颜色 */
  fill: string;
}

// 4 行主指标固定顺序;label 与 mockup 对齐。
const METRIC_ROWS: MetricRowSpec[] = [
  { key: "cpu",    label: "CPU",       fill: "#5e6ad2" },
  { key: "memory", label: "内存",       fill: "#10b981" },
  { key: "gpu",    label: "GPU",       fill: "#8b5cf6" },
  { key: "tasks",  label: "运行中任务", fill: "#0891b2" },
];

const NA = "—";

function formatMetric(used: number | null, quota: number | null, unit: string): string {
  const u = used === null || used === undefined ? NA : used;
  const q = quota === null || quota === undefined ? NA : quota;
  return unit ? `${u} / ${q} ${unit}` : `${u} / ${q}`;
}

function percent(used: number | null, quota: number | null): number {
  if (used === null || quota === null || quota === 0) return 0;
  return Math.min(100, Math.max(0, (used / quota) * 100));
}

function percentLabel(used: number | null, quota: number | null): string {
  if (used === null || quota === null) return NA;
  if (quota === 0) return "0%";
  return `${Math.round(percent(used, quota))}%`;
}

const GhostButton: React.FC<{
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ icon, onClick, disabled, title, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    title={title}
    style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      padding: "5px 12px",
      borderRadius: 6,
      fontSize: 12.5,
      color: disabled ? "#c4c6cc" : "#4b5563",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      transition: "border-color .15s, color .15s",
    }}
  >
    {icon}
    {children}
  </button>
);

const ProjectResourceCard: React.FC<Props> = ({
  item, loading, emoji, onOpenTrend, onEditQuota,
}) => {
  const hasError = Boolean(item.error);

  return (
    <Card
      style={{
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 2px rgba(0,0,0,.04)",
        minWidth: 0,
      }}
      styles={{ body: { padding: "20px 24px" } }}
    >
      {/* 卡头 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingBottom: 14,
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 4,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 15,
            fontWeight: 600,
            color: "#374151",
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 5,
              background: "#f5f5f7",
              color: "#6b6f76",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
            }}
          >
            {emoji}
          </span>
          {item.display_name}
        </span>
        <div style={{ flex: 1 }} />
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <GhostButton
            icon={<LineChartOutlined />}
            disabled
            title="用量趋势 · 即将上线"
          >
            用量趋势
          </GhostButton>
          <GhostButton
            icon={<EditOutlined />}
            onClick={onEditQuota}
            disabled={!onEditQuota}
            title={onEditQuota ? undefined : "请到「后台管理 → 任务类型管理」调整配额"}
          >
            编辑配额
          </GhostButton>
        </div>
      </div>

      {/* scope tag */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 16px" }}>
        <Tag color="processing" style={{ margin: 0 }}>
          {item.scope}
        </Tag>
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : hasError ? (
        <Alert
          type="warning"
          showIcon
          message="项目配额拉取失败"
          description={item.error}
        />
      ) : (
        <>
          {METRIC_ROWS.map((row) => {
            const metric = item.resources[row.key];
            const used = metric?.used ?? null;
            const quota = metric?.quota ?? null;
            const unit = metric?.unit ?? "";
            const w = percent(used, quota);
            const isZero = quota === 0 || quota === null;
            return (
              <div key={row.key} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: "#374151", fontWeight: 500 }}>{row.label}</span>
                  <span
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      color: "#6b6f76",
                      fontSize: 12.5,
                    }}
                  >
                    {formatMetric(used, quota, unit)}
                  </span>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 20,
                    background: "#f0f0f0",
                    margin: "6px 0 2px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${w}%`,
                      background: isZero ? "#d9d9d9" : row.fill,
                      borderRadius: 20,
                      transition: "width .3s",
                    }}
                  />
                </div>
                <div
                  style={{
                    textAlign: "right",
                    fontSize: 11,
                    color: "#8c8c8c",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.2,
                  }}
                >
                  {percentLabel(used, quota)}
                </div>
              </div>
            );
          })}

          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px dashed #e5e7eb",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              fontSize: 12,
              color: "#6b6f76",
            }}
          >
            <ParamRow k="最大并行子任务数" v={item.params.max_parallel_subtasks} />
            <ParamRow k="单 worker 任务数" v={item.params.tasks_per_worker} />
            <ParamRow k="单 Pod 最大并行任务数" v={item.params.tasks_per_pod} />
          </div>
        </>
      )}
    </Card>
  );
};

const ParamRow: React.FC<{ k: string; v: number | null }> = ({ k, v }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <span style={{ color: "#8c8c8c" }}>{k}:</span>
    <Text
      strong
      style={{
        color: "#374151",
        fontVariantNumeric: "tabular-nums",
        fontSize: 12,
      }}
    >
      {v === null || v === undefined ? NA : v}
    </Text>
  </div>
);

export default ProjectResourceCard;
